/**
 * ProcessingTimeline — horizontal stepper showing the contract extraction
 * pipeline state.
 *
 * Design follows the Aurora project-timeline pattern: each step is a
 * circular node on a horizontal line, with status colour + label + timestamp.
 *
 *   ✓ ──── ✓ ──── ✓ ──── ● ──── ○ ──── ○
 *   Up     Ext    Cls    Norm   Tasks  Ready
 *   12:34  12:35  12:35  …      pending pending
 *
 * Data model
 * ──────────
 * Reads `contract.processing` produced by the backend pipeline. Step order
 * mirrors `ProcessingStatus` in modules/contracts/firestore_schemas.py.
 * We don't have per-step timestamps yet — only `started_at` (uploaded) and
 * `completed_at` (terminal). Intermediate steps just show "—" until we add
 * step-timestamp tracking in the pipeline.
 *
 * Error state
 * ───────────
 * If status === 'error', we mark the current step as failed (red) and stop
 * advancing. `processing.error` is shown as a hoverable tooltip on the node.
 */

import React from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';

// Step definitions — order matters; status maps to the index.
const STEPS = [
  { key: 'uploaded',          label: 'Uploaded' },
  { key: 'extracting',        label: 'OCR' },
  { key: 'classifying',       label: 'Classifying' },
  { key: 'normalizing',       label: 'Extracting terms' },
  { key: 'extracting_tasks',  label: 'Tasks' },
  { key: 'ready',             label: 'Ready' },
];

// Map current status to the index of the step we're AT. 'ready' is past the
// last step (everything done). Unknown/null statuses default to 0.
function currentStepIdx(status) {
  if (!status) return 0;
  if (status === 'error') return -1;            // sentinel — handled specially
  const idx = STEPS.findIndex(s => s.key === status);
  return idx === -1 ? 0 : idx;
}

const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    const d = typeof iso === 'string' ? new Date(iso) : new Date(iso);
    if (Number.isNaN(d.valueOf())) return null;
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch (_) { return null; }
};

// One node — circle with icon + label + timestamp underneath.
const Node = ({ state, label, time, error }) => {
  const theme = useTheme();
  const palette = {
    done:     { bg: theme.palette.primary.main,  fg: '#ffffff', ring: 'transparent' },
    current:  { bg: theme.palette.primary.main + '22', fg: theme.palette.primary.main, ring: theme.palette.primary.main },
    pending:  { bg: 'transparent', fg: theme.palette.text.disabled, ring: theme.palette.divider },
    error:    { bg: theme.palette.error.main, fg: '#ffffff', ring: 'transparent' },
  }[state];

  const Icon =
    state === 'done'    ? CheckCircleRoundedIcon :
    state === 'current' ? HourglassTopRoundedIcon :
    state === 'error'   ? ErrorRoundedIcon :
                          RadioButtonUncheckedIcon;

  const tooltipText = state === 'error' && error
    ? error
    : (time ? `${label} · ${time}` : label);

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                 minWidth: 80, position: 'relative', zIndex: 1 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%',
          bgcolor: palette.bg,
          color: palette.fg,
          border: '2px solid', borderColor: palette.ring,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.3s ease',
          ...(state === 'current' && {
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}66` },
              '50%':      { boxShadow: `0 0 0 6px ${theme.palette.primary.main}00` },
            },
          }),
        }}>
          <Icon sx={{
            fontSize: 18,
            ...(state === 'current' && { animation: 'spin 2s linear infinite' }),
            '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(180deg)' } },
          }} />
        </Box>
        <Typography sx={{
          mt: 0.75, fontSize: '0.72rem', fontWeight: 600,
          color: state === 'pending' ? 'text.disabled' : 'text.primary',
          textAlign: 'center', whiteSpace: 'nowrap',
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.65rem', color: 'text.secondary',
          fontFamily: "'JetBrains Mono', monospace",
          minHeight: 14,
        }}>
          {time || (state === 'pending' ? '—' : '')}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// Connector line between nodes — fills proportionally as steps complete.
const Connector = ({ filled }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      flex: 1, height: 2, mx: 0.5, mt: '14px',
      bgcolor: filled ? theme.palette.primary.main : theme.palette.divider,
      transition: 'background-color 0.4s ease',
    }} />
  );
};

export default function ProcessingTimeline({ processing }) {
  const status      = processing?.status;
  const startedAt   = processing?.started_at;
  const completedAt = processing?.completed_at;
  const error       = processing?.error;
  const isError     = status === 'error';
  const currIdx     = currentStepIdx(status);

  // Decide each step's state and timestamp.
  const renderedSteps = STEPS.map((s, idx) => {
    let state;
    if (isError) {
      if (idx <  currIdx) state = 'done';
      if (idx === currIdx || (currIdx === -1 && idx === 0)) state = 'error';
      if (idx >  currIdx) state = 'pending';
    } else {
      if (idx <  currIdx) state = 'done';
      if (idx === currIdx) state = status === 'ready' ? 'done' : 'current';
      if (idx >  currIdx) state = 'pending';
    }
    // Only step 0 and last carry timestamps from the doc; others are blanks.
    let time = null;
    if (idx === 0)               time = fmtTime(startedAt);
    if (idx === STEPS.length - 1 && state === 'done') time = fmtTime(completedAt);

    return { ...s, state, time };
  });

  return (
    <Box sx={{
      p: 2, borderRadius: 1.5,
      border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
      display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap',
      overflowX: 'auto',
    }}>
      {renderedSteps.map((s, idx) => (
        <React.Fragment key={s.key}>
          <Node state={s.state} label={s.label} time={s.time} error={error} />
          {idx < renderedSteps.length - 1 && (
            <Connector filled={s.state === 'done' || (renderedSteps[idx + 1].state === 'done')} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}
